import React, { useContext, useEffect, useState } from 'react'
import API from '../../../../services/API'
import { GlobalContext, TenantSettingsContext } from '../../../../App.js'

//@MUI
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import Modal from '@mui/material/Modal'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import GroupAddIcon from '@mui/icons-material/GroupAdd'
import { Alert, Stack } from '@mui/material'
import defaultEmployeeTeams from '../../../../helpers/auth/_defaultEmployeeTeams.js'
import AddHomeWorkIcon from '@mui/icons-material/AddHomeWork'

const MiddleStack = ({ employee, userAccount, userID }) => {
  const { orgConfig } = useContext(TenantSettingsContext)
  const { baseLocationOptions } = useContext(GlobalContext)
  const [roles, setRoles] = useState(employee.enabledRoles)
  const [teams, setTeams] = useState(employee.teams)
  const [orgRoles, setOrgRoles] = useState([])
  const [orgTeams, setOrgTeams] = useState([])
  const [openPopup, setOpenPopup] = useState(false)
  const [selectedBaseLocations, setSelectedBaseLocations] = useState([])
  const [minimumBLWarning, setMinimumBLWarning] = useState(false)
  const baseLocationIDs = baseLocationOptions.map((x) => x._id)

  useEffect(() => {
    API.get('/settings?select=roles%20teams%20additionalTeams').then((res) => {
      if (res.status === 200) {
        setOrgRoles(res.data.roles)
        setOrgTeams([...defaultEmployeeTeams, ...res.data.additionalTeams])
        const expiredRoles = employee.enabledRoles.filter(
          (x) => !res.data.roles.includes(x),
        )
        setOrgRoles((prev) => [...prev, ...expiredRoles])
      }
    })
  }, [])

  const setDefaultBaseLocations = () => {
    setSelectedBaseLocations(
      Array.isArray(userAccount?.permittedBaseLocations)
        ? userAccount.permittedBaseLocations
            .map((id) => String(id))
            .filter((id) => baseLocationIDs.includes(id))
        : [],
    )
  }

  useEffect(() => {
    setDefaultBaseLocations()
  }, [employee, userAccount, baseLocationOptions])

  const activeTeams = orgTeams.reduce((accumulator, team) => {
    if (teams.includes(team)) {
      accumulator[team] = true
    } else {
      accumulator[team] = false
    }
    return accumulator
  }, {})

  const activeRoles = orgRoles.reduce((accumulator, role) => {
    if (roles.includes(role)) {
      accumulator[role] = true
    } else {
      accumulator[role] = false
    }
    return accumulator
  }, {})

  const handleRoleChange = (e) =>
    e.target.checked
      ? setRoles((prev) => [...prev, e.target.id])
      : setRoles(roles.filter((x) => x !== e.target.id))

  const handleTeamsChange = (e) =>
    e.target.checked
      ? setTeams((prev) => [...prev, e.target.id])
      : setTeams(teams.filter((x) => x !== e.target.id))

  const handleBaseLocationChange = (e) => {
    setMinimumBLWarning(false)
    if (selectedBaseLocations.length === 1 && !e.target.checked) {
      setMinimumBLWarning(true)
    }
    return e.target.checked
      ? setSelectedBaseLocations((prev) => [...prev, e.target.value])
      : setSelectedBaseLocations((prev) =>
          prev.filter((x) => x !== e.target.value),
        )
  }

  const handleSubmit = (e) => {
    if (!baseLocationOptions.length || !selectedBaseLocations.length) {
      setMinimumBLWarning(true)
      return
    }

    API.put(`/employee`, {
      updates: {
        _id: userID,
        employee: {
          teams,
          enabledRoles: roles,
        },
        permittedBaseLocations: selectedBaseLocations,
      },
    })
      .then((res) => res.status === 200 && window.location.reload())
      .catch((e) => console.log(e))
  }

  return (
    <Grid item lg={4} xs={12}>
      <Box display="flex" flexDirection="column" gap={4}>
        <Box display="flex" gap={1} alignItems="center">
          <Typography variant="smallGrey" width={50}>
            Roles:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {employee &&
              employee?.enabledRoles?.map((x) => (
                <Chip size="small" label={x} key={x} sx={{ borderRadius: 1 }} />
              ))}
            <GroupAddIcon
              fontSize="small"
              color="anPurple"
              onClick={() => setOpenPopup(true)}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <Typography variant="smallGrey" width={50}>
            Teams:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {employee &&
              employee.teams.map((x) => (
                <Chip size="small" label={x} key={x} sx={{ borderRadius: 1 }} />
              ))}
            <GroupAddIcon
              fontSize="small"
              color="anPurple"
              onClick={() => setOpenPopup(true)}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <Typography variant="smallGrey" width={100}>
            Base Locations:
          </Typography>
          <Box display="flex" gap={1}>
            {baseLocationOptions
              .filter((x) => selectedBaseLocations.includes(x._id))
              .map((x) => (
                <Chip
                  size="small"
                  label={x.name}
                  key={x._id}
                  sx={{ borderRadius: 1 }}
                />
              ))}
            <AddHomeWorkIcon
              fontSize="small"
              color="anPurple"
              onClick={() => setOpenPopup(true)}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
        </Box>
      </Box>
      <Modal open={openPopup} onClose={() => setOpenPopup(false)}>
        <Box sx={popupStyle}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box>
                <Typography variant="title2" fontSize={18}>
                  Roles
                </Typography>
              </Box>
              <Box padding={1} gap={1} display="flex" flexWrap="wrap">
                {Object.keys(activeRoles).map((role) => (
                  <Box
                    display="flex"
                    alignItems="center"
                    paddingRight={2}
                    borderRadius={1}
                    bgcolor={
                      roles.includes(role.trim()) ? '#E5FDD1' : '#f2f2f2'
                    }
                    key={role}
                  >
                    <Checkbox
                      checked={activeRoles[role]}
                      id={role}
                      onChange={handleRoleChange}
                      size="small"
                      color="success"
                    />
                    <Typography fontFamily="poppins" fontSize={14}>
                      {role}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Stack>
                <Typography variant="title2" fontSize={18}>
                  Management Teams
                </Typography>
              </Stack>
              <Box padding={1} gap={1} display="flex" flexWrap="wrap">
                {Object.keys(activeTeams).map((team) => (
                  <Box
                    display="flex"
                    alignItems="center"
                    paddingRight={2}
                    borderRadius={1}
                    bgcolor={teams.includes(team) ? '#E5FDD1' : '#f2f2f2'}
                    key={team}
                  >
                    <Checkbox
                      checked={activeTeams[team]}
                      id={team}
                      onChange={handleTeamsChange}
                      size="small"
                      color="success"
                    />
                    <Typography fontFamily="poppins" fontSize={14}>
                      {team}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Typography variant="caption">
                Teams allow supervisors/managers to access additional features
                throughout AmbuNet
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Stack>
                <Typography variant="title2" fontSize={18}>
                  Base Locations
                </Typography>
              </Stack>
              <Box padding={1} gap={1} display="flex" flexWrap="wrap">
                {baseLocationOptions.map((bl) => {
                  return (
                    <Box
                      display="flex"
                      alignItems="center"
                      paddingRight={2}
                      borderRadius={1}
                      bgcolor={
                        selectedBaseLocations.includes(bl._id)
                          ? '#E5FDD1'
                          : '#f2f2f2'
                      }
                      key={bl._id}
                    >
                      <Checkbox
                        checked={selectedBaseLocations.includes(bl._id)}
                        id={bl._id}
                        value={bl._id}
                        onChange={handleBaseLocationChange}
                        size="small"
                        color="success"
                      />
                      <Typography fontFamily="poppins" fontSize={14}>
                        {bl.name}
                      </Typography>
                    </Box>
                  )
                })}
              </Box>
              {minimumBLWarning && (
                <Box px={1}>
                  <Alert severity="error" variant="outlined">
                    Users must be assigned to at least one Base Location
                  </Alert>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} lg={6}>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => {
                  setOpenPopup(false)
                  setDefaultBaseLocations()
                }}
                fullWidth
              >
                Cancel
              </Button>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Button
                variant="contained"
                fullWidth
                color="success"
                size="small"
                onClick={handleSubmit}
                disabled={
                  minimumBLWarning ||
                  !baseLocationOptions.length ||
                  !selectedBaseLocations.length
                }
              >
                Submit
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </Grid>
  )
}

export default MiddleStack

const popupStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '80%', lg: '50%' },
  bgcolor: 'background.paper',
  borderRadius: 1,
  boxShadow: 24,
  borderColor: 'none',
  maxHeight: 500,
  overflow: 'auto',
  padding: 1,
  outline: 'none',
}
